import React, { useState, useEffect } from 'react';
import { X, Users } from 'lucide-react';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'success';
  duration?: number;
}

interface CollaborationToastProps {
  messages: ToastMessage[];
  onDismiss: (id: string) => void;
}

const CollaborationToast: React.FC<CollaborationToastProps> = ({ messages, onDismiss }) => {
  const [visibleMessages, setVisibleMessages] = useState<ToastMessage[]>([]);

  useEffect(() => {
    setVisibleMessages(messages);

    // 自動消失計時器
    messages.forEach(message => {
      const duration = message.duration || 5000; // 預設 5 秒
      setTimeout(() => {
        onDismiss(message.id);
      }, duration);
    });
  }, [messages, onDismiss]);

  if (visibleMessages.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 space-y-2">
      {visibleMessages.map(message => (
        <div
          key={message.id}
          className={`
            max-w-sm p-4 rounded-lg shadow-lg border transform transition-all duration-300 ease-in-out
            animate-in slide-in-from-right-2 
            ${message.type === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800' : ''}
            ${message.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''}
            ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : ''}
          `}
        >
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Users className={`
                w-5 h-5 
                ${message.type === 'info' ? 'text-blue-500' : ''}
                ${message.type === 'warning' ? 'text-yellow-500' : ''}
                ${message.type === 'success' ? 'text-green-500' : ''}
              `} />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium leading-5">
                {message.message}
              </p>
            </div>
            
            <button
              onClick={() => onDismiss(message.id)}
              className={`
                flex-shrink-0 rounded-md p-1 hover:bg-opacity-20 focus:outline-none focus:ring-2 focus:ring-offset-2
                ${message.type === 'info' ? 'hover:bg-blue-500 focus:ring-blue-500' : ''}
                ${message.type === 'warning' ? 'hover:bg-yellow-500 focus:ring-yellow-500' : ''}
                ${message.type === 'success' ? 'hover:bg-green-500 focus:ring-green-500' : ''}
              `}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CollaborationToast;